import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import {
  enablePush,
  getPushStatus,
  isPushSupported,
  type PushContext,
  type PushStatus,
} from '@/lib/pushClient';

const DISMISS_DAYS = 30;

function dismissKey(ctx: PushContext): string {
  return ctx.kind === 'admin'
    ? 'push-prompt-dismissed-at'
    : `push-prompt-dismissed-at:${ctx.token}`;
}
function readDismissedAt(ctx: PushContext): number {
  try {
    const raw = window.localStorage.getItem(dismissKey(ctx));
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}
function writeDismissedAt(ctx: PushContext, ts: number) {
  try {
    window.localStorage.setItem(dismissKey(ctx), String(ts));
  } catch {
    // ignore
  }
}

export default function PushEnablementPrompt({
  context = { kind: 'admin' },
}: {
  context?: PushContext;
} = {}) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (Notification.permission !== 'default') return;
    const dismissed = readDismissedAt(context);
    if (dismissed && Date.now() - dismissed < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
      return;
    }
    let cancelled = false;
    getPushStatus().then((s: PushStatus) => {
      if (cancelled) return;
      if (s === 'idle') setVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, [context]);

  if (!visible) return null;

  const dismiss = () => {
    writeDismissedAt(context, Date.now());
    setVisible(false);
  };
  const onEnable = async () => {
    setBusy(true);
    try {
      const next = await enablePush(context);
      if (next === 'subscribed') {
        setVisible(false);
      } else if (next === 'denied') {
        writeDismissedAt(context, Date.now());
        setVisible(false);
      } else if (next === 'not-configured') {
        alert('Push notifications are not configured on the server yet.');
        setVisible(false);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to enable push');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4">
      <div className="flex w-full max-w-md items-start gap-3 rounded-lg border border-indigo-800 bg-slate-900 p-3 text-sm shadow-lg">
        <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-indigo-900/60 text-indigo-200">
          <Bell size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-100">
            Enable push notifications
          </div>
          <div className="mt-0.5 text-xs text-slate-400">
            {context.kind === 'admin'
              ? "Get pinged when a new self-report arrives, when you're leading the train today, and when admins broadcast a message."
              : 'Get pinged for upcoming events, train duty and messages from your R4/R5.'}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onEnable}
              className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Enable
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="ml-1 rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
